// React e Hooks
import React, { useState, useEffect, useMemo, useCallback } from 'react';

// Material UI - Componentes Básicos
import {
  Typography,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
} from '@mui/material';

// Material UI - Tema
import { ThemeProvider } from '@mui/material/styles';

// Componentes de Layout
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import TabsContainer from '../components/Tabs/TabsContainer';
import PageContainer from '../components/Layout/PageContainer';
import WelcomeHeader from '../components/WelcomeHeader';
import ACConfigTab from '../components/Tabs/AC-Config';
import PosVendaMensagens from '../components/Tabs/PosVendaMensagens';
import AiDirectPreVenda from './AiDirectPreVenda';

// Componentes Funcionais
import ModoEspiao from '../components/ModoEspiao';
import FilterPanel from '../components/Filters/FilterPanel';
import LeadsTable from '../components/Table/LeadsTable';

// Componentes de Visualização
import StatusBarChart from '../components/Charts/StatusBarChart';
import DailyBarChart from '../components/Charts/DailyBarChart';
import GraficoMensagensPorDia from '../components/Charts/GraficoMensagensPorDia';

// Hooks Personalizados
import useAtendimentos from '../hooks/useAtendimentos';
import useContratosAtivos from '../hooks/useContratosAtivos';

// Utilitários
import {
  aggregateAtendimentosPorDia,
  aggregateAtendimentosPorStatus,
  aggregateMensagensPorDiaExato
} from '../utils/aggregation';

// Bibliotecas de Terceiros
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Configuração do tema customizado
import theme from '../components/theme/theme';

// Token Validation Components
import { useTokenValidation, TokenExpiredDialog } from '../utils/tokenValidation';

/**
 * Componente principal do Assistente Pós-Venda
 * Gerencia dashboard com métricas, tabela de gestão e modo espião
 * Nova versão com design responsivo e estilização moderna
 */
const AssistentePosVenda = () => {
  // ===== ESTADOS LOCAIS =====

  // ========== VALIDAÇÃO DE TOKEN ==========
  const { showExpiredDialog, countdown, redirectToHome } = useTokenValidation();

  // Estado para controle de abas
  const [tabIndex, setTabIndex] = useState(0);

  // Estados para o Modo Espião
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [, setContactsError] = useState(null);
  const [forceTokenExpired, setForceTokenExpired] = useState(false);

  // Estado para controle de erros
  const [, setError] = useState(null);

  // URL base da API, obtida das variáveis de ambiente
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const { contratosAtivos } = useContratosAtivos();

  const canAccessAiDirectTab = useMemo(() => contratosAtivos.some(
    (contrato) => Number(contrato?.idProduto ?? contrato?.id_produto) === 8
  ), [contratosAtivos]);

  const canAccessWhatsappTab = useMemo(() => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) return false;

      const decodedToken = jwtDecode(token);
      return ["2", "87"].includes(String(decodedToken.id_cliente));
    } catch (error) {
      console.error("Erro ao verificar acesso à aba Whatsapp:", error);
      return false;
    }
  }, []);

  const configTabIndex = canAccessAiDirectTab ? 4 : 3;
  const whatsappTabIndex = canAccessWhatsappTab ? configTabIndex + 1 : -1;
  const tabLabels = useMemo(() => {
    const labels = ["Dashboard", "Gestão", "Modo Espião"];
    if (canAccessAiDirectTab) labels.push("aiDirect");
    labels.push("Configurações");
    if (canAccessWhatsappTab) labels.push("Whatsapp");
    return labels;
  }, [canAccessAiDirectTab, canAccessWhatsappTab]);

  useEffect(() => {
    if (!canAccessWhatsappTab && tabIndex > configTabIndex) {
      setTabIndex(0);
    }
  }, [canAccessWhatsappTab, configTabIndex, tabIndex]);

  /**
   * Manipula mudança de aba
   * @param {Event} event - Evento de mudança de aba
   * @param {number} newValue - Índice da nova aba selecionada
   */
  const handleTabChange = useCallback((event, newValue) => {
    setTabIndex(newValue);
    setError(null); // Limpa erros ao trocar de aba
  }, []);

  /**
   * Manipula o clique em um WhatsAppId na tabela de atendimentos
   * Seleciona o contato e redireciona para a aba Modo Espião
   * @param {Object} atendimento - Objeto contendo dados do atendimento clicado
   */
  const handleWhatsAppIdClick = useCallback((atendimento) => {
    if (atendimento) {
      setSelectedContact(atendimento);
      setTabIndex(2); // Muda para a aba Modo Espião (índice 2)
    }
  }, []);

  /**
   * Hook personalizado que gerencia o estado e as operações relacionadas aos atendimentos
   */
  const {
    //atendimentos,
    filteredAtendimentos,
    loading: loadingAtendimentos,
    dateFilter,
    setDateFilter,
    situacaoFilter,
    setSituacaoFilter,
    whatsAppIdFilter,
    setWhatsAppIdFilter,
    dataFilter,
    setDataFilter,
    handleClearFilters,
  } = useAtendimentos(tabIndex, baseURL, setForceTokenExpired);

  /**
   * Effect para buscar contatos para o Modo Espião quando esta aba estiver ativa
   */
  useEffect(() => {
    if (tabIndex === 2) {
      setLoadingContacts(true);
      setContactsError(null);

      const fetchContacts = async () => {
        try {
          const token = localStorage.getItem('jwtToken');

          if (!token) {
            throw new Error('Token de autenticação não encontrado');
          }

          const decodedToken = jwtDecode(token);
          const idCliente = decodedToken.id_cliente;

          if (!idCliente) {
            throw new Error('ID do cliente não encontrado no token');
          }

          const response = await axios.get(`${baseURL}/produto/posv/lista-contatos`, {
            params: { idCliente },
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000 // Timeout de 10 segundos
          });

          if (response.data) {
            setContacts(Array.isArray(response.data) ? response.data : []);
          } else {
            setContacts([]);
          }
        } catch (error) {
          if (
            error.response?.status === 401 ||
            error.response?.data?.message?.toLowerCase().includes("token") // ou "expired"
          ) {
            setForceTokenExpired(true); // Força abrir o diálogo
          }
          console.error('Erro ao buscar contatos:', error);
          setContactsError('Erro ao carregar contatos. Tente novamente.');
          setContacts([]);
        } finally {
          setLoadingContacts(false);
        }
      };

      fetchContacts();
    }
  }, [tabIndex, baseURL]);

  // ===== CÁLCULOS E AGREGAÇÕES DE DADOS =====

  const aggregatedStatusData = useMemo(() => {
    if (!Array.isArray(filteredAtendimentos)) {
      return aggregateAtendimentosPorStatus([]);
    }
    return aggregateAtendimentosPorStatus(filteredAtendimentos);
  }, [filteredAtendimentos]);

  const aggregatedByDay = useMemo(
    () => aggregateAtendimentosPorDia(filteredAtendimentos, dateFilter),
    [filteredAtendimentos, dateFilter]
  );

  const mensagensPorDiaExato = useMemo(() => {
    if (!Array.isArray(filteredAtendimentos)) {
      return { labels: [], data: [] };
    }
    return aggregateMensagensPorDiaExato(filteredAtendimentos, dateFilter);
  }, [filteredAtendimentos, dateFilter]);

  // Valores derivados do aggregatedStatusData
  const { total: totalAtendimentos, emAberto: emAbertoCount, concluidos: concluidosCount } = aggregatedStatusData;

  return (
    <ThemeProvider theme={theme}>
      {/* Layout principal com design responsivo */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // Coluna em mobile, linha em desktop
          minHeight: '100vh',
          width: '100vw',
          maxWidth: '100%',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >

        {/* Sidebar responsivo */}
        <Sidebar
          aria-label="Menu de navegação lateral"
          sx={{
            width: { xs: '100%', md: 280 },
            flexShrink: 0,
            order: { xs: 2, md: 1 },
          }}
        />

        {/* Área de conteúdo principal */}
        <Box
          sx={{
            flexGrow: 1,
            order: { xs: 1, md: 2 },
            width: { xs: '100%', md: 'auto' },
          }}
        >
          {/* Cabeçalho responsivo */}
          <Header
            title="Pós-Venda"
            aria-label="Pós-Venda"
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
            }}
          />

          {/* Container principal do conteúdo */}
          <PageContainer
            sx={{
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 1.25, md: 1.5 },
            }}
          >
            {/* Header de boas-vindas com gradiente */}
            <WelcomeHeader
              title='Pós-Venda'
              subtitle=''
            />

            {/* Abas de navegacao */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: { xs: 2, md: 3 },
                border: '1px solid rgba(0,0,0,0.05)',
                px: { xs: 1, md: 1.2 },
                mb: { xs: 2, md: 2.4 },
              }}
            >
              <TabsContainer
                tabIndex={tabIndex}
                onTabChange={handleTabChange}
                tabLabels={tabLabels}
                marginB={0}
              />
            </Box>

            {/* Container principal com abas */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: { xs: 2, md: 3 },
                boxShadow: {
                  xs: '0 2px 10px rgba(0,0,0,0.08)',
                  md: '0 4px 20px rgba(0,0,0,0.08)'
                },
                border: '1px solid rgba(0,0,0,0.05)',
                mb: { xs: 2, md: 0 },
                overflow: 'hidden',
              }}
            >
              {/* Conteúdo das abas com padding responsivo */}
              <Box sx={{ p: { xs: 2, sm: 3, md: 3 }, pb: { xs: 6, sm: 3, md: 3 } }}>

                {/* Conteúdo da aba Dashboard */}
                {tabIndex === 0 && (
                  <Box>
                    {/* Seletor de filtro aprimorado */}
                    <Box
                      sx={{
                        mb: 2,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                        Período de Análise
                      </Typography>
                      <TextField
                        select
                        label="Filtrar por período"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          minWidth: { xs: '100%', sm: 200 },
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      >
                        <MenuItem value="Todos">Todos os períodos</MenuItem>
                        <MenuItem value="Mês atual">Mês atual</MenuItem>
                        <MenuItem value="Últimos 7 dias">Últimos 7 dias</MenuItem>
                        <MenuItem value="Últimos 30 dias">Últimos 30 dias</MenuItem>
                        <MenuItem value="Últimos 3 meses">Últimos 3 meses</MenuItem>
                        <MenuItem value="Últimos 6 meses">Últimos 6 meses</MenuItem>
                        <MenuItem value="Últimos 12 meses">Últimos 12 meses</MenuItem>
                      </TextField>
                    </Box>

                    {/* Conteúdo dos gráficos */}
                    {loadingAtendimentos ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: 400,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        <CircularProgress size={48} sx={{ mb: 2 }} />
                        <Typography variant="body1">Carregando métricas...</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        {/* Gráfico de Status */}
                        <StatusBarChart
                          data={aggregatedStatusData.data}
                          labels={aggregatedStatusData.labels}
                          total={totalAtendimentos}
                          emAberto={emAbertoCount}
                          concluidos={concluidosCount}
                        />

                        {/* Gráfico Diário */}
                        <DailyBarChart
                          data={aggregatedByDay.data}
                          labels={aggregatedByDay.labels}
                        />

                        {/* Gráfico de Mensagens por Dia Exato */}
                        <GraficoMensagensPorDia
                          data={mensagensPorDiaExato.data}
                          labels={mensagensPorDiaExato.labels}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {/* Conteúdo da aba Gestão */}
                {tabIndex === 1 && (
                  <Box
                    sx={{
                      mt: { xs: 2, md: 3 },
                      mb: { xs: 3, md: 4 },
                      minHeight: '100vh',
                    }}
                  >
                    {/* Painel de filtros com design moderno */}
                    <Box>
                      <FilterPanel
                        dateFilter={dateFilter}
                        setDateFilter={setDateFilter}
                        situacaoFilter={situacaoFilter}
                        setSituacaoFilter={setSituacaoFilter}
                        whatsAppIdFilter={whatsAppIdFilter}
                        setWhatsAppIdFilter={setWhatsAppIdFilter}
                        dataFilter={dataFilter}
                        setDataFilter={setDataFilter}
                        onClearFilters={handleClearFilters}
                      />
                    </Box>

                    {/* Container da tabela com estilização moderna */}
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        borderRadius: { xs: 2, md: 3 },
                        p: { xs: 2, sm: 3, md: 3 },
                        boxShadow: {
                          xs: '0 2px 10px rgba(0,0,0,0.08)',
                          md: '0 4px 20px rgba(0,0,0,0.08)'
                        },
                        border: '1px solid rgba(0,0,0,0.05)',
                        mb: { xs: 2, md: 0 },
                      }}
                    >
                      {/* Estado de carregamento com design moderno */}
                      {loadingAtendimentos ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 400,
                            color: theme.palette.text.secondary,
                            py: 6,
                          }}
                        >
                          <CircularProgress
                            size={60}
                            sx={{
                              mb: 3,
                              color: theme.palette.primary.main
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 1,
                              fontWeight: 500,
                              color: theme.palette.primary.main
                            }}
                          >
                            Carregando atendimentos...
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.7,
                              textAlign: 'center',
                              maxWidth: 300
                            }}
                          >
                            Aguarde enquanto buscamos seus dados
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          {/* Tabela de atendimentos */}
                          <LeadsTable
                            atendimentos={filteredAtendimentos}
                            onWhatsAppClick={handleWhatsAppIdClick}
                          />
                        </>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Conteúdo da aba Modo Espião */}
                {tabIndex === 2 && (
                  <Box>
                    <Box
                      sx={{
                        bgcolor: 'rgba(102, 126, 234, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(102, 126, 234, 0.1)',
                        overflow: 'hidden',
                      }}
                    >
                      <ModoEspiao
                        contacts={contacts}
                        selectedContact={selectedContact}
                        onSelectContact={setSelectedContact}
                        currentTab="outras-abas"
                        endpointPrefix="posv"
                        loadingContacts={loadingContacts}
                        setForceTokenExpired={setForceTokenExpired}
                      />
                    </Box>
                  </Box>
                )}

                {canAccessAiDirectTab && tabIndex === 3 && (
                  <AiDirectPreVenda
                    setForceTokenExpired={setForceTokenExpired}
                    productOrigin="POSV"
                    productLabel="Pos-venda"
                    historyEndpoint="/produto/posv/lista-mensagens"
                    historyClientParam="idCliente"
                    historyWhatsAppParam="WhatsAppId"
                    referenceLabel="atendimento"
                  />
                )}

                {/* Conteúdo da aba Configurações */}
                {tabIndex === configTabIndex && (
                  <ACConfigTab produto="posv" />
                )}

                {/* Conteúdo da aba Whatsapp */}
                {canAccessWhatsappTab && tabIndex === whatsappTabIndex && (
                  <PosVendaMensagens />
                )}
              </Box>
            </Box>
          </PageContainer>
        </Box>
      </Box>
      {/* ========== DIALOG DE TOKEN EXPIRADO - GLOBAL ========== */}
      {/* 
        POSICIONADO AQUI para aparecer independente da aba ativa
        Este dialog ficará sempre no nível mais alto do componente
      */}
      <TokenExpiredDialog
        open={showExpiredDialog || forceTokenExpired}
        countdown={countdown}
        onRedirect={redirectToHome}
      />
    </ThemeProvider>
  );
};

export default AssistentePosVenda;
